import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";

export interface Devotional {
  date: string;
  title: string;
  topic: string;
  memoryVerse: {
    reference: string;
    text: string;
  };
  text: {
    reference: string;
    content: string;
  };
  message: string;
  prayer: string;
  finalWord: {
    reference: string;
    text: string;
  };
  spouseToSpouse: {
    question: string;
    reconciliation: string;
    prayers: string[];
  };
  morningPrayers: {
    confessions: string[];
    prayerPoints: string[];
  };
  eveningPrayers: {
    confessions: string[];
    prayerPoints: string[];
  };
  authors: {
    names: string;
    image: string;
    bio: string;
  };
  hymn: {
    title: string;
    audioUrl: string;
  };
}

const splitPipeSeparatedString = (value: string | null): string[] => {
  if (!value) return [];
  return value.split('|').map(item => item.trim());
};

const mapDatabaseToDevotional = (data: any): Devotional => {
  
  const devotional = {
    date: data.date,
    title: data.title,
    topic: data.topic,
    memoryVerse: {
      reference: data.memory_verse_reference,
      text: data.memory_verse_text,
    },
    text: {
      reference: data.text_reference,
      content: data.text_content,
    },
    message: data.message,
    prayer: data.prayer,
    finalWord: {
      reference: data.final_word_reference,
      text: data.final_word_text,
    },
    spouseToSpouse: {
      question: data.spouse_to_spouse_question,
      reconciliation: data.spouse_to_spouse_reconciliation || "",
      prayers: data.spouse_to_spouse_prayers || [],
    },
    morningPrayers: {
      confessions: splitPipeSeparatedString(data.morning_prayers_confessions),
      prayerPoints: splitPipeSeparatedString(data.morning_prayers_points),
    },
    eveningPrayers: {
      confessions: splitPipeSeparatedString(data.evening_prayers_confessions),
      prayerPoints: splitPipeSeparatedString(data.evening_prayers_points),
    },
    authors: {
      names: data.authors_names,
      image: data.authors_image,
      bio: data.authors_bio,
    },
    hymn: {
      title: data.hymn_title,
      audioUrl: data.hymn_audio_url,
    },
  };

  
  return devotional;
};

// Helper to check if a year is a leap year
const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

// Get all years that have devotional data in the database
const getAvailableYears = async (): Promise<number[]> => {
  try {
    const { data, error } = await supabase
      .from("devotionals")
      .select("date")
      .order("date", { ascending: false });
    
    if (error || !data) return [];
    
    const years = [...new Set(data.map(d => parseISO(d.date).getFullYear()))];
    return years.sort((a, b) => b - a); // Most recent first
  } catch {
    return [];
  }
};

export const getDevotionalForDate = async (date: Date): Promise<Devotional | undefined> => {
  const dateStr = format(date, "yyyy-MM-dd");
  const monthDay = format(date, "MM-dd"); // e.g., "02-29", "12-31"
  const requestedYear = date.getFullYear();

  try {
    // 1) Try exact date match (ideal case)
    const { data: exactMatch, error: exactError } = await supabase
      .from("devotionals")
      .select("*")
      .eq("date", dateStr)
      .maybeSingle();

    if (exactError) {
      console.error("Error fetching devotional (exact match):", exactError);
    }

    if (exactMatch) {
      return mapDatabaseToDevotional(exactMatch);
    }

    // 2) If device date is ahead/behind (common on mobile), fall back to the latest
    // devotional on or before the requested date.
    const { data: latestAvailable, error: latestError } = await supabase
      .from("devotionals")
      .select("*")
      .lte("date", dateStr)
      .order("date", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (latestError) {
      console.error("Error fetching devotional (latest available):", latestError);
    }

    if (latestAvailable) {
      return mapDatabaseToDevotional(latestAvailable);
    }

    // 3) As a last resort, find content from previous years with same month-day
    const availableYears = await getAvailableYears();

    for (const year of availableYears) {
      if (year >= requestedYear) continue; // Skip current/future years

      // Special handling for Feb 29
      if (monthDay === "02-29") {
        // Only look for Feb 29 content in leap years
        if (isLeapYear(year)) {
          const feb29DateStr = `${year}-02-29`;
          const { data: feb29Data } = await supabase
            .from("devotionals")
            .select("*")
            .eq("date", feb29DateStr)
            .maybeSingle();

          if (feb29Data) {
            return mapDatabaseToDevotional(feb29Data);
          }
        }
        // If no Feb 29 content found, continue searching other years
        continue;
      }

      // For regular dates, try to match month-day from previous years
      const historicalDateStr = `${year}-${monthDay}`;
      const { data: historicalData } = await supabase
        .from("devotionals")
        .select("*")
        .eq("date", historicalDateStr)
        .maybeSingle();

      if (historicalData) {
        return mapDatabaseToDevotional(historicalData);
      }
    }

    return undefined;
  } catch (error) {
    console.error("Error fetching devotional:", error);
    return undefined;
  }
};

export const getDevotionalsByDateRange = async (startDate: Date, endDate: Date): Promise<Devotional[]> => {
  try {
    const { data, error } = await supabase
      .from("devotionals")
      .select("*")
      .gte("date", format(startDate, "yyyy-MM-dd"))
      .lte("date", format(endDate, "yyyy-MM-dd"))
      .order("date", { ascending: false });

    if (error) {
      console.error("Error fetching devotionals:", error);
      return [];
    }

    return data ? data.map(mapDatabaseToDevotional) : [];
  } catch (error) {
    console.error("Error fetching devotionals:", error);
    return [];
  }
};

export const getPreviousDevotionals = async (date: Date, count: number = 3): Promise<Devotional[]> => {
  const endDate = new Date(date);
  const startDate = new Date(date);
  startDate.setDate(startDate.getDate() - count);
  
  return getDevotionalsByDateRange(startDate, endDate);
};
